/* eslint-disable consistent-return */
/* eslint-disable no-nested-ternary */
import { useCallback, useContext, useEffect, useState } from 'react';

import { Grid } from '@mui/material';
import { Button, Input, Typography } from 'design_system/src';
import { useNavigate } from 'react-router-dom';

import Counter from 'components/hocs/Counter/Counter';
import { informativeMessage } from 'components/hocs/messages/messages';
import { LightTooltip } from 'components/hocs/tooltip/Tooltip';
import { RequireRole } from 'components/layouts/main/sidebar/Sidebar';
import { ReactComponent as Calendar } from 'design_system/src/static/icons/date.svg';
import { WhitelistNameEnum } from 'models/whitelist/interfaces/whitelist.interface';
import {
	updateCreditLineFinancialForm,
	updateFinancialForm,
} from 'models/financial-form/controllers/financial-form.controller';
import {
	CanceledReasonValues,
	DevolutionReasonValues,
	FinancialFormTypeEnum,
	FormStatus,
	FormStatusValues,
	RejectedReasonValues,
	SalesRankEnum,
	salesRankValues,
	VehiclePurposeValues,
} from 'models/financial-form/enums/financial-form.enums';
import { getLatestPreOffer } from 'models/financial-form/helpers/preOffer.helpers';
import { Roles } from 'models/ozonio/enums/ozonio.enum';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { momentLocal } from 'models/shared/helpers/dates';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import { priceFormatFinancial } from 'models/shared/helpers/prices';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import '../../../ozoner.scss';
import { ozonerContext } from '../../OzonerContext';
import { PROFILE_TIER_COLOR, PROFILE_TIER_LABEL } from '../RiskProfileOzoner';
import ReachSection from './1.ReachSection';
import PendingSection from './2.PendingSection';
import ValidationSection from './3.ValidationSection';
import ApprovedSection from './4.ApprovedSection';
import ScheduledSection from './5.ScheduledSection';
import ExpiredSection from './ExpiredSection';
import FullPaymentSection from './FullPaymentSection';
import RejectedSection from './RejectedSection';
import {
	CollectionResponsible,
	CollectionSegmentation,
	LoanSegmentation,
	PartnershipSegmentation,
} from './Segmentations';
import { CancelAction } from './SolicitudActions';

const noCancelStatuses = [
	FormStatus.scheduled,
	FormStatus.completed,
	FormStatus.canceled,
	FormStatus.rejected,
	FormStatus.rejectedKyc,
	FormStatus.expired,
];

function SolicitudOzoner() {
	const {
		ozoner,
		updateFormData,
		updateComment,
		financialForm,
		validCurp,
		canValidateCurp,
		sellerOptions,
		referrals,
	} = useContext(ozonerContext);

	// -----------------DATA STATES------------------- //
	const [comment, setComment] = useState<string>('');

	const [loadingResponsible, setLoadingResponsible] = useState(false);
	const [loadingVehiclePurpose, setLoadingVehiclePurpose] = useState(false);

	useEffect(() => {
		setComment(ozoner?.comment ?? '');
	}, [ozoner]);

	const navigate = useNavigate();

	const handleChangeResponsible = (e: any) => {
		if (financialForm?.form?._id && e.target.value) {
			setLoadingResponsible(true);
			const isCreditLine =
				financialForm.form.type === FinancialFormTypeEnum.creditLine;
			const update = isCreditLine
				? updateCreditLineFinancialForm(financialForm.form._id, {
						sellResponsible: e.target.value,
				  })
				: updateFinancialForm(financialForm.form._id, {
						loanApplication: {
							sellResponsible: e.target.value,
						},
				  });
			update
				.then((newForm) => {
					updateFormData(newForm);
					setLoadingResponsible(false);
					toasterNotification({
						msg: 'Solicitud Exitosa',
						style: 'light',
						toasterType: toasterOptions.success,
					});
				})
				.catch((err) => {
					setLoadingResponsible(false);
					commonErrorHandler(err);
				});
		}
	};

	const handleChangeVehiclePurpose = (e: any) => {
		if (financialForm?.form?._id && e.target.value) {
			setLoadingVehiclePurpose(true);
			const isCreditLine =
				financialForm.form.type === FinancialFormTypeEnum.creditLine;
			const update = isCreditLine
				? updateCreditLineFinancialForm(financialForm.form._id, {
						vehiclePurpose: e.target.value,
				  })
				: updateFinancialForm(financialForm.form._id, {
						vehiclePurpose: e.target.value,
				  });
			update
				.then((newForm) => {
					updateFormData(newForm);
					setLoadingVehiclePurpose(false);
					toasterNotification({
						msg: 'Solicitud Exitosa',
						style: 'light',
						toasterType: toasterOptions.success,
					});
				})
				.catch((err) => {
					setLoadingVehiclePurpose(false);
					commonErrorHandler(err);
				});
		}
	};

	// const activateContado = () => {
	// 	setModalOption('approve_contado');
	// 	handleEventGoogle(
	// 		'BTN_Pb_request_create_contract_contado',
	// 		'Panda Solicitudes',
	// 		'click in create contract to request button',
	// 	);
	// };

	const renderFinancialFormActions = useCallback(() => {
		if (!financialForm?.form) return;
		switch (financialForm.form.status) {
			case FormStatus.pending:
				if (financialForm.form.type === FinancialFormTypeEnum.fullPayment)
					return <FullPaymentSection />;
				if (financialForm.form.type === FinancialFormTypeEnum.creditLine)
					return <ApprovedSection />;
				return <ReachSection />;
			case FormStatus.contacted:
				return <PendingSection />;
			case FormStatus.inValidation:
				return <ValidationSection />;
			case FormStatus.approved:
				return <ApprovedSection />;

			case FormStatus.scheduled:
				return <ScheduledSection />;
			case FormStatus.rejected:
			case FormStatus.rejectedKyc:
				return <RejectedSection />;
			case FormStatus.canceled:
				return informativeMessage(
					'Solicitud cancelada por:',
					financialForm.form.loanApplication?.canceledReason
						? CanceledReasonValues[
								financialForm.form.loanApplication?.canceledReason
						  ] ?? '--'
						: '--',
					'neutral_1000',
					'neutral_400',
				);
			case FormStatus.expired:
				return <ExpiredSection />;

			default:
				return null;
		}
	}, [ozoner, financialForm]);

	const getWebpageLink = () => {
		let prefix = '';
		if (process.env.REACT_APP_API_BASE_URL?.includes('dev')) {
			prefix = 'dev.';
		}
		if (process.env.REACT_APP_API_BASE_URL?.includes('qa')) {
			prefix = 'qa.';
		}
		navigator.clipboard
			.writeText(
				`https://${prefix}ozon.mobi/documentos-en-proceso?email=${encodeURIComponent(
					ozoner?.email ?? '',
				)}`,
			)
			.then(() => {
				toasterNotification({
					toasterType: toasterOptions.success,
					msg: 'Copiado al portapapeles',
					style: 'light',
				});
			})
			.catch((error) => {
				toasterNotification({
					toasterType: toasterOptions.error,
					msg: `Error al copiar el contenido: ${error}`,
					style: 'light',
				});
			});
	};

	const goToOzoner = (e: any, id?: string) => {
		e.stopPropagation();
		if (id) {
			navigate(`/ozoner/${id}`);
		}
	};

	return financialForm?.form ? (
		<Grid container direction="row" spacing={3} className="h_100_per p_t_md">
			<Grid item xs={3}>
				<div className="bg_neutral_0 border_neutral_400 border_solid border_1 br_sm">
					{renderFinancialFormActions()}

					{financialForm?.form?.status !== FormStatus.pending &&
					!noCancelStatuses.includes(financialForm?.form?.status) ? (
						<CancelAction />
					) : null}
				</div>
			</Grid>

			<Grid item xs={9}>
				{canValidateCurp && !validCurp
					? informativeMessage(
							'El CURP del ozoner esta mal',
							'El CURP que registra truora es diferente al de panda',
							'red_300',
							'red_200',
					  )
					: null}
				{financialForm && !financialForm.vehicleAvailable
					? informativeMessage(
							'Vehículo no disponible',
							'El vehículo a sido apartado o entregado a otro ozoner, por favor seleccione un nuevo vehículo para esta solicitud',
							'neutral_800',
							'yellow_100',
					  )
					: null}

				{financialForm?.form?.loanApplication?.devolutionReasons?.length
					? informativeMessage(
							'Solicitud devuelta por:',
							DevolutionReasonValues[
								financialForm.form.loanApplication.devolutionReasons[
									financialForm.form.loanApplication.devolutionReasons.length -
										1
								]?.reason
							] ?? '--',
							'neutral_0',
							'secondary_400',
					  )
					: null}

				{financialForm?.form?.loanApplication?.rejectedReason
					? informativeMessage(
							'Solicitud rechazada por:',
							RejectedReasonValues[
								financialForm.form.loanApplication.rejectedReason
							] ?? '--',
							'red_300',
							'red_200',
					  )
					: null}

				<Grid container direction="row" spacing={1} className="">
					<Grid item xs={4}>
						<div className="br_xxs bg_neutral_0 flex_align_center p_x_md p_y_xs dim_100_per display_flex flex_gap_md border_1 border_neutral_400 border_solid ">
							<Calendar className="dim_xxl text_primary_100 border_transparent" />
							<div className="flex_grow_3">
								<Typography scale="medium" weight="600">
									{momentLocal(financialForm?.form?.createdAt).format(
										'DD/MM/yyyy hh:mm',
									)}
								</Typography>
								<Typography scale="xsmall" weight="400">
									Fecha de creacion
								</Typography>
							</div>
							{!financialForm.form.loanApplication?.contactMethod &&
								financialForm.form.status === FormStatus.pending &&
								financialForm.form.type !==
									FinancialFormTypeEnum.creditLine && (
									<div className="flex_center_col">
										<Counter
											date={financialForm?.form?.createdAt}
											colorMap={[
												{ color: 'neutral_1000', maxValue: 30 },
												{ color: 'yellow_100', maxValue: 120 },
												{ color: 'red_300', maxValue: Infinity },
											]}
										/>
										<Typography
											scale="xxsmall"
											weight="400"
											className="text_center"
										>
											sin contactar
										</Typography>
									</div>
								)}
						</div>
					</Grid>
					<Grid item xs={4}>
						<div className="br_xxs bg_neutral_0 flex_align_center p_x_md p_y_xs dim_100_per display_flex flex_gap_md border_1 border_neutral_400 border_solid ">
							<div
								className={`br_circle dim_xxl bg_${
									FormStatusValues[financialForm.form.status].color
								}`}
							/>
							<div className="flex_grow_3">
								<Typography scale="medium" weight="600">
									{FormStatusValues[financialForm.form.status].title}
								</Typography>
								<Typography scale="xsmall" weight="400">
									Estado de solicitud{' '}
									{financialForm?.form?.statusHistoric?.length
										? `desde:
										${momentLocal(
											financialForm?.form?.statusHistoric[
												financialForm.form.statusHistoric.length - 1
											].updatedAt,
										).format('DD/MM/yyyy')}`
										: ''}
								</Typography>
							</div>
						</div>
					</Grid>
					{financialForm?.form.type === FinancialFormTypeEnum.loan && (
						<Grid item xs={4}>
							<div className="br_xxs bg_neutral_0 flex_align_center p_x_md p_y_xs dim_100_per display_flex flex_gap_md border_1 border_neutral_400 border_solid ">
								<div
									className={`br_circle dim_xxl bg_${
										salesRankValues[
											financialForm?.form?.loanApplication?.salesRank ??
												SalesRankEnum.wood
										].color
									}`}
								/>
								<div className="flex_grow_3">
									<Typography scale="medium" weight="600">
										{
											salesRankValues[
												financialForm?.form?.loanApplication?.salesRank ??
													SalesRankEnum.wood
											].title
										}
									</Typography>
									<Typography scale="xsmall" weight="400">
										Segmentación
									</Typography>
								</div>
							</div>
						</Grid>
					)}
					{financialForm?.form.type === FinancialFormTypeEnum.loan &&
						(() => {
							const preOffer = getLatestPreOffer(
								financialForm.form.loanApplication?.preOffers,
							);
							if (!preOffer) return null;
							const profileTier = preOffer.scoring?.profileTier ?? '';
							return (
								<Grid item xs={4}>
									<div className="br_xxs bg_neutral_0 flex_align_center p_x_md p_y_xs dim_100_per display_flex flex_gap_md border_1 border_neutral_400 border_solid ">
										<Typography
											scale="medium"
											weight="600"
											textColor={
												(PROFILE_TIER_COLOR[profileTier] ??
													'neutral_600') as any
											}
										>
											{PROFILE_TIER_LABEL[profileTier] ?? profileTier ?? '--'}
										</Typography>
										<div className="flex_grow_3">
											<Typography scale="medium" weight="600">
												Calculado el{' '}
												{momentLocal(preOffer.createdAt).format('DD/MM/yyyy')}
											</Typography>
											<Typography scale="xsmall" weight="400">
												Tier de perfil (pre-oferta)
											</Typography>
										</div>
									</div>
								</Grid>
							);
						})()}
					{financialForm?.form.type === FinancialFormTypeEnum.creditLine &&
						(() => {
							const profileTier = 'B1';
							return (
								<Grid item xs={4}>
									<div className="br_xxs bg_neutral_0 flex_align_center p_x_md p_y_xs dim_100_per display_flex flex_gap_md border_1 border_neutral_400 border_solid ">
										<Typography
											scale="medium"
											weight="600"
											textColor={
												(PROFILE_TIER_COLOR[profileTier] ??
													'neutral_600') as any
											}
										>
											{PROFILE_TIER_LABEL[profileTier] ?? profileTier ?? '--'}
										</Typography>
										<div className="flex_grow_3">
											<Typography
												scale="medium"
												weight="600"
												textColor="green_300"
											>
												{financialForm.form.whitelistName ??
													WhitelistNameEnum.creditLine}
											</Typography>
											<Typography scale="xsmall" weight="400">
												Credit line PayJoy
											</Typography>
										</div>
									</div>
								</Grid>
							);
						})()}
					{FormStatusValues[financialForm.form.status].order >= 3 &&
						financialForm?.form.type === FinancialFormTypeEnum.loan &&
						(() => {
							const latestApprovedPreOffer = getLatestPreOffer(
								(financialForm.form.loanApplication?.preOffers ?? []).filter(
									(preOffer) => preOffer.status === 'approved',
								),
							);
							return (
								<Grid item xs={4}>
									<div className="br_xxs bg_neutral_0 dim_100_per flex_center p_x_md p_y_xs flex_gap_md border_1 border_neutral_400 border_solid flex_justify_between ">
										<div>
											<Typography scale="medium" weight="600" className="">
												Monto max:
											</Typography>
											<Typography scale="xsmall" weight="400" className="">
												Capacidad de financiamiento máxima
											</Typography>
										</div>

										<Typography
											scale="large"
											weight="600"
											textColor="green_300"
											className=""
										>
											{latestApprovedPreOffer?.maxFinancingCapacity
												? priceFormatFinancial(
														latestApprovedPreOffer.maxFinancingCapacity,
														0,
												  )
												: '--'}
										</Typography>
									</div>
								</Grid>
							);
						})()}
				</Grid>
				{financialForm?.form.type === FinancialFormTypeEnum.loan && (
					<Grid container direction="row" spacing={3} className="p_t_md">
						<Grid item xs={6} className="p_md">
							<div className="br_xxs border_1 border_neutral_400 border_solid ">
								<div className="bg_primary_25 p_x_md p_y_xs border_b_solid border_1 border_neutral_400">
									<Typography scale="medium" weight="600">
										Acciones ventas
									</Typography>
								</div>
								<div className="p_x_md p_y_sm border_b_solid border_1 border_neutral_400 ">
									<div className="display_flex flex_justify_between flex_align_center flex_gap_md">
										<Typography
											scale="medium"
											weight="400"
											className="w_50_per"
										>
											Responsable Ventas:
										</Typography>
										<div className="w_50_per">
											<Input
												title=""
												disabled={
													loadingResponsible ||
													financialForm.form.status === FormStatus.completed
												}
												name="Responsable"
												placeholder="Ingresa el Ozonio"
												value={
													financialForm?.form?.loanApplication?.sellResponsible
												}
												onChange={handleChangeResponsible}
												type="select"
												options={sellerOptions}
												variant="default"
												className="w_100_per"
											/>
										</div>
									</div>
								</div>
								<div className="p_x_md p_y_sm border_b_solid border_1 border_neutral_400 ">
									<div className="display_flex flex_justify_between flex_align_center flex_gap_md">
										<Typography
											scale="medium"
											weight="400"
											className="w_50_per"
										>
											Uso de la moto:
										</Typography>
										<div className="w_50_per">
											<Input
												title=""
												disabled={
													loadingVehiclePurpose ||
													financialForm.form.status === FormStatus.completed ||
													financialForm.form.status === FormStatus.scheduled
												}
												name="VehiclePurpose"
												placeholder="Selecciona el uso"
												value={financialForm.form.vehiclePurpose}
												onChange={handleChangeVehiclePurpose}
												className="w_100_per"
												type="select"
												options={Object.entries(VehiclePurposeValues).map(
													([key, value]) => ({
														value: key,
														label: value,
													}),
												)}
												variant="default"
											/>
										</div>
									</div>
								</div>
								<div className="p_x_md p_y_lg border_b_solid border_1 border_neutral_400 ">
									<div className="display_flex flex_justify_between flex_align_center flex_gap_md">
										<Typography
											scale="medium"
											weight="400"
											className="w_50_per"
										>
											Link para cargar documentos:
										</Typography>
										<div className="w_50_per">
											<Button
												scale="small"
												color="primary"
												variant="outline"
												onClick={getWebpageLink}
												className="w_100_per"
											>
												Copiar Link Carga Docs.
											</Button>
										</div>
									</div>
								</div>
								{FormStatusValues[financialForm.form.status].order >= 2 && (
									<div className="p_x_md p_y_lg border_b_solid border_1 border_neutral_400 ">
										<div className="display_flex flex_justify_between flex_align_center flex_gap_md">
											<Typography scale="medium" weight="600">
												Metodo de contacto:
											</Typography>
											<Typography
												scale="medium"
												weight="400"
												className="text_capitalize"
											>
												{financialForm.form.loanApplication?.contactMethod}
											</Typography>
										</div>
									</div>
								)}
							</div>
						</Grid>
						{FormStatusValues[financialForm.form.status].order >= 2 && (
							<Grid item xs={6} className="p_md">
								<div className="br_xxs border_1 border_neutral_400 border_solid h_100_per">
									<div className="bg_primary_25 p_x_md p_y_xs border_b_solid border_1 border_neutral_400">
										<Typography scale="medium" weight="600">
											Segmentacion
										</Typography>
									</div>
									<div className="p_x_md p_y_sm border_b_solid border_1 border_neutral_400 ">
										<PartnershipSegmentation />
									</div>
									{FormStatusValues[financialForm.form.status].order >= 3 && (
										<div className="p_x_md p_y_sm border_b_solid border_1 border_neutral_400 ">
											<LoanSegmentation />
										</div>
									)}
									{FormStatusValues[financialForm.form.status].order >= 5 && (
										<RequireRole
											authRoles={[Roles.ADMIN, Roles.COLLECTOR_MANAGER]}
										>
											<>
												<div className="p_x_md p_y_sm border_b_solid border_1 border_neutral_400 ">
													<CollectionResponsible />
												</div>
												<div className="p_x_md p_y_sm border_b_solid border_1 border_neutral_400 ">
													<CollectionSegmentation />
												</div>
											</>
										</RequireRole>
									)}
								</div>
							</Grid>
						)}
						{financialForm.referredBy || referrals.length > 0 ? (
							<Grid item xs={6} className="p_md">
								<div className="br_xxs border_1 border_neutral_400 border_solid h_100_per">
									<div className="bg_primary_25 p_x_md p_y_xs border_b_solid border_1 border_neutral_400">
										<Typography scale="medium" weight="600">
											Programa de referidos
										</Typography>
									</div>
									{referrals.length > 0 ? (
										<div className="p_x_md p_y_sm border_b_solid border_1 border_neutral_400 ">
											<Typography
												scale="medium"
												weight="600"
												className="p_b_xs"
											>
												Refirió a:
											</Typography>
											{referrals.map((referral) => (
												<div
													className="display_flex p_b_xs flex_justify_between"
													key={referral._id}
												>
													<Typography
														scale="medium"
														weight="400"
														className="p_b_xs text_link"
														onClick={(e) => goToOzoner(e, referral.ozoner._id)}
													>
														{referral.ozoner.firstName ?? ''}{' '}
														{referral.ozoner.middleName ?? ''}{' '}
														{referral.ozoner.lastName ?? ''}{' '}
														{referral.ozoner.secondLastName ?? ''}
													</Typography>
													<LightTooltip
														title={
															FormStatusValues[referral.status]?.tooltip ??
															'Sin informacion'
														}
													>
														<div>
															<Typography
																scale="small"
																weight="600"
																className={`br_xs p_x_md p_y_xxxs text_center text_neutral_0 pos_relative
                                        bg_${
																					FormStatusValues[referral.status]
																						?.color
																				}_nocontrast`}
															>
																{FormStatusValues[referral.status]?.title}
															</Typography>
														</div>
													</LightTooltip>
												</div>
											))}
										</div>
									) : null}
									{financialForm.referredBy ? (
										<div className="p_x_md p_y_sm border_b_solid border_1 border_neutral_400 ">
											<Typography
												scale="medium"
												weight="600"
												className="p_b_xs"
											>
												Fue referido por:
											</Typography>
											<div className="display_flex p_b_xs flex_justify_between">
												<Typography
													scale="medium"
													weight="400"
													className="p_b_xs text_link"
													onClick={(e) =>
														goToOzoner(e, financialForm.referredBy?.ozoner._id)
													}
												>
													{financialForm.referredBy?.ozoner.firstName ?? ''}{' '}
													{financialForm.referredBy?.ozoner.middleName ?? ''}{' '}
													{financialForm.referredBy?.ozoner.lastName ?? ''}{' '}
													{financialForm.referredBy?.ozoner.secondLastName ??
														''}
												</Typography>
												<LightTooltip
													title={
														FormStatusValues[financialForm.referredBy?.status]
															?.tooltip ?? 'Sin informacion'
													}
												>
													<div>
														<Typography
															scale="small"
															weight="600"
															className={`br_xs p_x_md p_y_xxxs text_center text_neutral_0 pos_relative
                                        bg_${
																					FormStatusValues[
																						financialForm.referredBy?.status
																					]?.color
																				}_nocontrast`}
														>
															{
																FormStatusValues[
																	financialForm.referredBy?.status
																]?.title
															}
														</Typography>
													</div>
												</LightTooltip>
											</div>
										</div>
									) : null}
								</div>
							</Grid>
						) : null}
					</Grid>
				)}
				{financialForm?.form.type === FinancialFormTypeEnum.creditLine && (
					<Grid container direction="row" spacing={3} className="p_t_md">
						<Grid item xs={6} className="p_md">
							<div className="br_xxs border_1 border_neutral_400 border_solid ">
								<div className="bg_primary_25 p_x_md p_y_xs border_b_solid border_1 border_neutral_400">
									<Typography scale="medium" weight="600">
										Acciones ventas
									</Typography>
								</div>
								<div className="p_x_md p_y_sm border_b_solid border_1 border_neutral_400 ">
									<div className="display_flex flex_justify_between flex_align_center flex_gap_md">
										<Typography scale="medium" weight="400">
											Responsable Ventas:
										</Typography>
										<Input
											title=""
											disabled={
												loadingResponsible ||
												financialForm.form.status === FormStatus.completed
											}
											name="Responsable"
											placeholder="Ingresa el Ozonio"
											value={(financialForm?.form as any)?.sellResponsible}
											onChange={handleChangeResponsible}
											className="w_100_per"
											type="select"
											options={sellerOptions}
											variant="default"
										/>
									</div>
								</div>
								<div className="p_x_md p_y_sm border_b_solid border_1 border_neutral_400 ">
									<div className="display_flex flex_justify_between flex_align_center flex_gap_md">
										<Typography scale="medium" weight="400">
											Uso de la moto:
										</Typography>
										<Input
											title=""
											disabled={
												loadingVehiclePurpose ||
												financialForm.form.status === FormStatus.completed ||
												financialForm.form.status === FormStatus.scheduled
											}
											name="VehiclePurpose"
											placeholder="Selecciona el uso"
											value={(financialForm?.form as any)?.vehiclePurpose}
											onChange={handleChangeVehiclePurpose}
											className="w_100_per"
											type="select"
											options={Object.entries(VehiclePurposeValues).map(
												([key, value]) => ({
													value: key,
													label: value,
												}),
											)}
											variant="default"
										/>
									</div>
								</div>
							</div>
						</Grid>
					</Grid>
				)}
				<Grid container direction="row" spacing={1} className="">
					<Grid item xs={12} className="">
						<Typography
							scale="large"
							weight="600"
							textColor="neutral_900"
							className="m_y_md"
						>
							Observaciones del ozoner :
						</Typography>
						<textarea
							className="observaciones w_100_per p_md border_1 border_solid border_neutral_400"
							placeholder="Agregue todas las observaciones del ozoner que se detecten"
							value={comment}
							onChange={(e: any) => setComment(e.target.value)}
						/>
						{comment !== ozoner?.comment && (
							<div className="display_flex flex_justify_end">
								<Button
									scale="small"
									variant="ghost"
									onClick={() => updateComment(comment)}
								>
									Guardar Cambios
								</Button>
								<Button
									scale="small"
									variant="ghost"
									onClick={() => setComment(ozoner?.comment ?? '')}
								>
									Cancelar
								</Button>
							</div>
						)}
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	) : null;
}
export default SolicitudOzoner;
